import { useRef, useState } from 'react';
import { Image, Loader2, Star, Tag, Trash2, Upload } from 'lucide-react';
import { uploadApi } from '@/api/uploadApi';
import { normalizeUploadedImageUrl } from '@/utils/imageUrls';
import { useToast } from '@/hooks/useToast';
import type { ProductFormState } from './types';
import { generateSkuPreview } from './types';

interface StepBasicsProps {
  state: ProductFormState;
  updateState: (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => void;
  nameError?: string;
}

export function StepBasics({ state, updateState, nameError }: StepBasicsProps) {
  const { success: showSuccess, error: showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const imageUrls = [
    ...(state.primaryImageUrl ? [state.primaryImageUrl] : []),
    ...state.imageUrls.filter((url) => url !== state.primaryImageUrl),
  ];

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadApi.uploadProductImage(file);
      updateState((prev) => {
        const nextUrls = [...prev.imageUrls.filter((url) => url !== uploaded.fileUrl), uploaded.fileUrl];
        return {
          ...prev,
          primaryImageUrl: prev.primaryImageUrl || uploaded.fileUrl,
          imageUrls: nextUrls,
        };
      });
      showSuccess('Product photo uploaded');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (url: string) => {
    updateState((prev) => {
      const nextUrls = prev.imageUrls.filter((item) => item !== url);
      const nextPrimary = prev.primaryImageUrl === url ? nextUrls[0] ?? null : prev.primaryImageUrl;
      return { ...prev, primaryImageUrl: nextPrimary, imageUrls: nextUrls };
    });
  };

  const makePrimary = (url: string) => {
    updateState((prev) => ({
      ...prev,
      primaryImageUrl: url,
      imageUrls: [url, ...prev.imageUrls.filter((item) => item !== url)],
    }));
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-warm-sand">
        Start with what you&apos;re calling this product. You can classify and configure the rest next.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          Product Name <span className="text-danger-400">*</span>
        </label>
        <input
          type="text"
          value={state.name}
          onChange={(e) => updateState((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Enter product name..."
          autoFocus
          className={`w-full px-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450 ${
            nameError ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600'
          }`}
        />
        {nameError && <p className="mt-1.5 text-sm text-danger-400">{nameError}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          SKU <span className="text-xs text-gray-600 dark:text-warm-sand/50">(optional)</span>
        </label>
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
          <input
            type="text"
            value={state.sku}
            onChange={(e) => updateState((prev) => ({ ...prev, sku: e.target.value }))}
            placeholder={state.name ? generateSkuPreview(state.name) : 'Auto-generated from name'}
            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          Description
        </label>
        <textarea
          value={state.description}
          onChange={(e) => updateState((prev) => ({ ...prev, description: e.target.value }))}
          rows={3}
          placeholder="Product description..."
          className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450 resize-none"
        />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-850 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-warm-cream">
              <Image className="h-4 w-4" />
              Product Photos
            </div>
            <p className="mt-1 text-xs text-gray-600 dark:text-warm-sand">
              Upload product photos now. They will be renamed from the product name after save.
            </p>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-amber-450 px-4 py-2 text-sm font-semibold text-charcoal-900 transition hover:bg-amber-400 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload Photo
          </button>
        </div>

        {imageUrls.length > 0 ? (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {imageUrls.map((url) => {
              const normalizedUrl = normalizeUploadedImageUrl(url) ?? url;
              const primary = state.primaryImageUrl === url;
              return (
                <div key={url} className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-charcoal-700 dark:bg-charcoal-900">
                  <div className="flex h-28 items-center justify-center bg-gray-100 dark:bg-charcoal-950">
                    <img src={normalizedUrl} alt="Product upload" className="h-full w-full object-contain" />
                  </div>
                  <div className="flex items-center justify-between gap-1 p-2">
                    <button
                      type="button"
                      onClick={() => makePrimary(url)}
                      className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs ${primary ? 'bg-amber-100 text-amber-800 dark:bg-amber-450/15 dark:text-amber-300' : 'text-gray-600 hover:bg-gray-100 dark:text-warm-sand dark:hover:bg-charcoal-800'}`}
                    >
                      <Star className="h-3.5 w-3.5" />
                      {primary ? 'Primary' : 'Set'}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImage(url)}
                      className="rounded-lg p-1.5 text-gray-500 transition hover:bg-red-50 hover:text-red-600 dark:text-warm-sand dark:hover:bg-red-500/10 dark:hover:text-red-300"
                      aria-label="Remove photo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600 dark:border-charcoal-700 dark:text-warm-sand">
            No product photos uploaded yet.
          </div>
        )}
      </div>
    </div>
  );
}
